<template>
  <div class="flex h-auto bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700">
      <div class="flex items-center justify-center space-x-3">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20" />
      </div>
      <Navbar></Navbar>
    </aside>

    <div class="flex-1 flex flex-col">
      <header class="h-16 bg-gray-800 flex items-center justify-between px-6 shadow-md bg-gradient-to-b from-gray-800 to-gray-900 border-b border-gray-700">
        <h2 class="text-xl font-semibold">CDN Management</h2>
        <button
          @click="showAddModal = true"
          class="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
        >
          + Add CDN Endpoint
        </button>
      </header>

      <main class="p-6 flex-1">
        <div
          class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
          rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition"
        >
          <h3 class="text-lg font-semibold mb-4">Your CDN Endpoints</h3>

          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-700">
                <th class="p-3">Origin</th>
                <th class="p-3">Zone</th>
                <th class="p-3">Status</th>
                <th class="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="cdn in cdnData"
                :key="cdn.id"
                class="border-b border-gray-600"
              >
                <td class="p-3">{{ cdn.origin || 'N/A' }}</td>
                <td class="p-3">{{ cdn.type }}</td>
                <td class="p-3">
                  <span
                    class="px-2 py-1 rounded text-sm"
                    :class="cdn.status === 'active' ? 'bg-green-600' : 'bg-red-600'"
                  >
                    {{ cdn.status }}
                  </span>
                </td>
                <td class="p-3">
                  <button
                    @click="removeCDN(cdn.id)"
                    class="bg-red-600 px-3 py-1 rounded hover:bg-red-700"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>

    <!-- Add CDN Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="bg-gray-800 p-6 rounded-xl shadow-md w-96">
        <h3 class="text-lg font-semibold mb-4">Add New CDN Endpoint</h3>

        <!-- Zone Selector -->
        <label class="block mb-2">Zone Type:</label>
        <select
          v-model="newCDN.type"
          class="w-full p-2 rounded bg-gray-700 text-white mb-4"
        >
          <option value="pull">Pull Zone</option>
          <option value="push">Push Zone</option>
        </select>

        <!-- Shared Field -->
        <label class="block mb-2">Label:</label>
        <input
          v-model="newCDN.label"
          type="text"
          class="w-full p-2 rounded bg-gray-700 text-white mb-4"
        />

        <!-- Pull Zone Specific -->
        <div v-if="newCDN.type === 'pull'">
          <label class="block mb-2">Origin URL:</label>
          <input
            v-model="newCDN.origin"
            type="text"
            class="w-full p-2 rounded bg-gray-700 text-white mb-4"
          />
        </div>

        <!-- Features -->
        <div class="mb-4">
          <label class="block font-semibold mb-2">Features:</label>
          <div class="space-y-2">
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="newCDN.features.cors" />
              <span>CORS</span>
            </label>
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="newCDN.features.gzip" />
              <span>Gzip</span>
            </label>
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="newCDN.features.blockAiBots" />
              <span>Block AI Bots</span>
            </label>
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="newCDN.features.blockMaliciousBots" />
              <span>Block Potential Malicious Bots</span>
            </label>
            <!-- Custom Domain Checkbox -->
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="newCDN.features.customDomainEnabled" />
              <span>Use Custom Domain</span>
            </label>

            <!-- Show input if checked -->
            <div v-if="newCDN.features.customDomainEnabled" class="pl-6">
              <input
                v-model="newCDN.customDomain"
                type="text"
                placeholder="Enter custom domain"
                class="w-full p-2 rounded bg-gray-700 text-white"
              />
            </div>
          </div>
        </div>

        <!-- Pricing Info -->
        <p class="text-sm text-gray-400 mb-4">$10 per month + Usage Fees</p>

        <!-- Actions -->
        <div class="flex justify-end space-x-3">
          <button
            @click="showAddModal = false"
            class="bg-gray-600 px-4 py-2 rounded"
          >
            Cancel
          </button>
          <button
            @click="addCDN"
            class="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from "@/components/nav.vue";
import axios from "axios";

export default {
  name: "CDNPage",
  components: { Navbar },
  data() {
    return {
      cdnData: [],
      showAddModal: false,
      newCDN: {
        type: "pull",
        label: "",
        origin: "",
        features: {
          cors: false,
          gzip: false,
          blockAiBots: false,
          blockMaliciousBots: false,
          customDomainEnabled: false,
        },
        customDomain: "",
      },
    };
  },
  methods: {
    async fetchCDNData() {
      try {
        const res = await axios.get("https://api.syniac.com:3000/api/cdn/get", {
          withCredentials: true,
        });
        this.cdnData = res.data;
      } catch (error) {
        console.error("Error fetching CDN data:", error);
      }
    },
    async addCDN() {
      try {
        await axios.post("https://api.syniac.com:3000/api/cdn/add", this.newCDN, {
          withCredentials: true,
        });
        this.fetchCDNData();
        this.showAddModal = false;
        this.resetForm();
      } catch (error) {
        console.error("Error adding CDN:", error);
      }
    },
    async removeCDN(id) {
      if (!confirm("Delete this CDN?")) return;
      try {
        await axios.delete(`https://api.syniac.com:3000/api/cdn/delete/${id}`, {
          withCredentials: true,
        });
        this.fetchCDNData();
      } catch (error) {
        console.error("Error removing CDN:", error);
      }
    },
    resetForm() {
      this.newCDN = {
        type: "pull",
        label: "",
        origin: "",
        features: {
          cors: false,
          gzip: false,
          blockAiBots: false,
          blockMaliciousBots: false,
          customDomainEnabled: false,
        },
        customDomain: "",
      };
    },
  },
  mounted() {
    this.fetchCDNData();
  },
};
</script>
